"use client";

import * as React from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Heritage() {
  const [activeFilter, setActiveFilter] = React.useState<"all" | "new">("all");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    if (searchOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [searchOpen]);
  return <section data-section-id="3960" id="heritage-hero" className="dark bg-background text-foreground relative min-h-[80vh] w-full overflow-hidden">
      {}
      <div className="relative z-20 flex items-center justify-between px-6 pt-8 md:px-12 lg:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          MISS YVONNE / Heritage
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Search products" className="border border-border/60 bg-card/40 backdrop-blur-md hover:bg-card/60">
            <Search className="size-4" />
          </Button>
          <CartIcon variant="ghost" size="icon" iconSize="size-4" className="border border-border/60 bg-card/40 backdrop-blur-md hover:bg-card/60" />
        </div>
      </div>

      {}
      <div className="relative grid grid-cols-1 gap-12 px-6 pt-12 pb-20 md:px-12 lg:grid-cols-12 lg:gap-0 lg:px-16 lg:pt-16">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute top-12 bottom-20 left-[58.333%] hidden w-px lg:block" style={{
        background: "linear-gradient(to bottom, transparent 0%, var(--primary) 20%, var(--primary) 80%, transparent 100%)",
        boxShadow: "0 0 12px var(--primary)"
      }} />

        {}
        <div ref={leftRef} className={`lg:col-span-7 lg:pr-16 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase text-muted-foreground" style={{
          letterSpacing: "0.24em"
        }}>
            <span className="inline-block size-1.5" style={{
            background: "var(--primary)",
            boxShadow: "0 0 8px var(--primary)"
          }} />
            <span>Est. 1965 — Ocean Springs</span>
          </div>

          {}
          <h1 className="mt-10 font-default font-bold uppercase leading-[0.85] text-foreground" style={{
          fontSize: "clamp(72px, 14vw, 200px)",
          letterSpacing: "-0.04em"
        }}>
            <span className="block">Built</span>
            <span className="block">
              By Hand
              <span style={{
              color: "var(--secondary)",
              textShadow: "0 0 24px var(--secondary)"
            }}>
                .
              </span>
            </span>
          </h1>

          {}
          <p className="mt-10 max-w-xl font-default italic text-muted-foreground" style={{
          fontSize: "22px",
          lineHeight: 1.5
        }}>
            Wild-caught seafood, expertly cut and delivered with care from our vessel to your table.
          </p>

          {}
          <div className="mt-14 flex flex-wrap items-center gap-3">
            <Button variant="ghost" onClick={() => setActiveFilter("all")} className={`h-10 rounded-none px-5 font-mono text-[11px] uppercase backdrop-blur-md transition-all ${activeFilter === "all" ? "bg-secondary text-white" : "bg-card/20 text-muted-foreground hover:bg-card/30 hover:text-foreground"}`} style={{
            letterSpacing: "0.2em",
            border: activeFilter === "all" ? "1px solid var(--secondary)" : "1px solid var(--border)"
          }}>
              Shop Seafood
            </Button>
            <Button variant="ghost" onClick={() => setActiveFilter("new")} className={`h-10 rounded-none px-5 font-mono text-[11px] uppercase backdrop-blur-md transition-all ${activeFilter === "new" ? "bg-secondary text-white" : "bg-card/20 text-muted-foreground hover:bg-card/30 hover:text-foreground"}`} style={{
            letterSpacing: "0.2em",
            border: activeFilter === "new" ? "1px solid var(--secondary)" : "1px solid var(--border)"
          }}>
              Our Story
            </Button>
          </div>

          {}
          <p className="mt-8 font-default italic text-sm text-muted-foreground">
            Proven by time, delivered fresh.
          </p>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-5 lg:pl-16 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            {}
            <div className="mb-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <span>Catch / 001</span>
              <span style={{
              color: "var(--secondary)"
            }}>—</span>
            </div>

            {}
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-card" style={{
            boxShadow: "0px 60px 100px -20px hsl(0 0% 0% / 0.8), 0px 30px 60px -30px hsl(0 0% 0% / 0.6)"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/miss_yvonne_vessel_deck.webp" alt="The Miss Yvonne fishing vessel deck at sunrise" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              {}
              <div className="pointer-events-none absolute top-3 left-3 size-3 border-t border-l border-foreground/40" />
              <div className="pointer-events-none absolute top-3 right-3 size-3 border-t border-r border-foreground/40" />
              <div className="pointer-events-none absolute bottom-3 left-3 size-3 border-b border-l border-foreground/40" />
              <div className="pointer-events-none absolute bottom-3 right-3 size-3 border-b border-r border-foreground/40" />
            </div>

            {}
            <div className="mt-6 flex items-end justify-between gap-4">
              <div>
                <p className="font-default italic text-foreground" style={{
                fontSize: "16px"
              }}>
                  Wild-Caught Tuna
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  Gulf Coast Selection
                </p>
              </div>
              <div className="flex items-center gap-2 font-mono text-[11px] text-foreground">
                <span>$45</span>
                <ArrowRight className="size-3" style={{
                color: "var(--secondary)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative z-10 flex items-center justify-between border-t border-border/60 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground md:px-12 lg:px-16">
        <span>30° 24' 42" N / 88° 49' 42" W</span>
        <span className="hidden md:inline">Ocean Springs, MS</span>
        <span style={{
        color: "var(--secondary)"
      }}>● Fresh</span>
      </div>

      {}
      {searchOpen && <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-border/60 px-6 py-6 md:px-12 lg:px-16">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              Search Our Catch
            </span>
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search" className="">
              <X className="size-5" />
            </Button>
          </div>
          <div className="flex flex-1 items-start justify-center px-6 pt-16 md:px-12 lg:px-16">
            <div className="w-full max-w-2xl">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search tuna, salmon, sampler boxes..." className="w-full" />
              <p className="mt-6 font-default italic text-sm text-muted-foreground">
                Press <span className="font-mono not-italic">ESC</span> to close.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}